import dayjs, { Dayjs } from 'dayjs';
import type { ReminderStateType } from '../../services/ReminderService';

export type ReminderViewMode = 'PAST' | 'ALL' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'FUTURE';

interface ReminderDateRange {
  startDate?: string;
  endDate?: string;
}

export interface ReminderFilters extends ReminderDateRange {
  stateList?: ReminderStateType[];
  'estimatedDurationMinutes.specified'?: boolean;
}

interface ReminderInitialState {
  viewMode: ReminderViewMode;
  filters: ReminderFilters;
}

const DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
const REMINDER_SCOPE_MAP: Record<string, ReminderViewMode> = {
  prev: 'PAST',
  today: 'DAILY',
  next: 'FUTURE',
};

export const getDateRangeForMode = (mode: ReminderViewMode, now: Dayjs = dayjs()): ReminderDateRange => {
  if (mode === 'PAST') {
    return {
      startDate: undefined,
      endDate: now.startOf('day').subtract(1, 'second').format(DATE_TIME_FORMAT),
    };
  }

  if (mode === 'DAILY') {
    return {
      startDate: now.startOf('day').format(DATE_TIME_FORMAT),
      endDate: now.endOf('day').format(DATE_TIME_FORMAT),
    };
  }

  if (mode === 'FUTURE') {
    return {
      startDate: now.add(1, 'day').startOf('day').format(DATE_TIME_FORMAT),
      endDate: undefined,
    };
  }

  if (mode === 'WEEKLY') {
    return {
      startDate: now.startOf('week').format(DATE_TIME_FORMAT),
      endDate: now.endOf('week').format(DATE_TIME_FORMAT),
    };
  }

  if (mode === 'MONTHLY') {
    return {
      startDate: now.startOf('month').format(DATE_TIME_FORMAT),
      endDate: now.endOf('month').format(DATE_TIME_FORMAT),
    };
  }

  return {
    startDate: undefined,
    endDate: undefined,
  };
};

export const resolveReminderViewModeFromScope = (scope?: string | null): ReminderViewMode =>
  REMINDER_SCOPE_MAP[(scope || '').trim().toLowerCase()] || 'DAILY';

export const resolveReminderStateListFromQuery = (
  stateParam?: string | null
): ReminderStateType[] | undefined => {
  const normalizedState = (stateParam || '').trim().toUpperCase();
  if (normalizedState === 'PENDING') {
    return ['PENDING'];
  }
  return undefined;
};

export const resolveBooleanFilterFromQuery = (param?: string | null): boolean | undefined => {
  const normalizedParam = (param || '').trim().toLowerCase();
  if (normalizedParam === 'true') {
    return true;
  }
  if (normalizedParam === 'false') {
    return false;
  }
  return undefined;
};

export const buildReminderInitialStateFromSearchParams = (
  searchParams: URLSearchParams,
  now: Dayjs = dayjs()
): ReminderInitialState => {
  const viewMode = resolveReminderViewModeFromScope(searchParams.get('scope'));
  const stateList = resolveReminderStateListFromQuery(searchParams.get('state'));
  const estimatedDurationMinutesSpecified = resolveBooleanFilterFromQuery(
    searchParams.get('estimatedDurationMinutes.specified')
  );

  return {
    viewMode,
    filters: {
      ...getDateRangeForMode(viewMode, now),
      ...(stateList ? { stateList } : {}),
      ...(estimatedDurationMinutesSpecified !== undefined
        ? { 'estimatedDurationMinutes.specified': estimatedDurationMinutesSpecified }
        : {}),
    },
  };
};

export const buildClearedReminderFilters = (
  viewMode: ReminderViewMode,
  now: Dayjs = dayjs()
): ReminderFilters => ({
  ...getDateRangeForMode(viewMode, now),
});
